import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Eye, EyeOff } from 'lucide-react'
import { Form, Formik } from 'formik'
import { useState } from 'react'
import {
  changePasswordInitialValues,
  changePasswordValidationSchema,
} from '@/pages/ProfileScreen/validation'
import { changeUserPassword } from '@/redux/asyncThunks/user'
import { useAppDispatch } from '@/redux/hooks'
import { toast } from 'sonner'

function ChangePassword() {
  const dispatch = useAppDispatch()

  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false)
  const handleSubmit = async (values: typeof changePasswordInitialValues) => {
    const formData = {
      new_password1: values.password,
      new_password2: values.confirmPassword,
    }

    try {
      const resultAction = await dispatch(changeUserPassword(formData))
      if (changeUserPassword.rejected.match(resultAction)) {
        const errorMessages = JSON.parse(resultAction.payload as string)[
          'new_password2'
        ]
        for (const message of errorMessages) {
          toast.error(message)
        }
      } else {
        toast.success(resultAction.payload.detail)
      }
    } catch (error) {
      console.error(error)
    }
  }
  return (
    <div className='flex w-3/4 flex-col md:w-full'>
      <Card className='w-full max-w-md border-none shadow-none'>
        <CardHeader className='space-y-3 flex flex-col p-0'>
          <CardTitle className='text-lg text-[#023572] font-bold mb-2'>
            Change Password
          </CardTitle>
        </CardHeader>
        <CardContent className='px-0'>
          <Formik
            initialValues={changePasswordInitialValues}
            validationSchema={changePasswordValidationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              isSubmitting,
            }) => (
              <Form className='space-y-4'>
                <div className='space-y-2'>
                  <Label
                    className={`text-base font-normal ${
                      touched.password && errors.password
                        ? 'text-red-500'
                        : 'text-gray-700'
                    }`}
                    htmlFor='password'
                  >
                    New Password
                  </Label>
                  <div className='relative'>
                    <Input
                      className={`h-10 placeholder:text-base placeholder:text-[#99A2B0] bg-[#FAFCFF] ${
                        touched.password && errors.password
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                      id='password'
                      name='password'
                      type={showPassword ? 'text' : 'password'}
                      placeholder='At least 8 characters'
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      className='absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent'
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <Eye className='h-4 w-4 text-gray-400' />
                      ) : (
                        <EyeOff className='h-4 w-4 text-gray-400' />
                      )}
                    </Button>
                  </div>
                  {touched.password && errors.password && (
                    <div className='text-red-500 text-sm flex gap-2'>
                      <img src='/icons/alert-circle.svg' alt='Warning' />
                      {errors.password}
                    </div>
                  )}
                </div>

                <div className='space-y-2'>
                  <Label
                    className={`text-base font-normal ${
                      touched.confirmPassword && errors.confirmPassword
                        ? 'text-red-500'
                        : 'text-gray-700'
                    }`}
                    htmlFor='confirmPassword'
                  >
                    Confirm Password
                  </Label>
                  <div className='relative'>
                    <Input
                      className={`h-10 placeholder:text-base placeholder:text-[#99A2B0] bg-[#FAFCFF] ${
                        touched.confirmPassword && errors.confirmPassword
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                      id='confirmPassword'
                      name='confirmPassword'
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder='At least 8 characters'
                      value={values.confirmPassword}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      className='absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent'
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? (
                        <Eye className='h-4 w-4 text-gray-400' />
                      ) : (
                        <EyeOff className='h-4 w-4 text-gray-400' />
                      )}
                    </Button>
                  </div>
                  {touched.confirmPassword && errors.confirmPassword && (
                    <div className='text-red-500 text-sm flex gap-2'>
                      <img src='/icons/alert-circle.svg' alt='Warning' />
                      {errors.confirmPassword}
                    </div>
                  )}
                </div>

                <div className='flex justify-between items-center'>
                  <Button
                    type='submit'
                    disabled={isSubmitting}
                    className='h-[38px] text-base font-normal bg-dark-blue hover:bg-dark-blue-hovered'
                  >
                    Change Password
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </div>
  )
}

export default ChangePassword
